/* eslint-disable react/jsx-no-target-blank */
import {Spinner, TextInput} from '@volkswagen-onehub/components-core';
import React, {useMemo, useState} from 'react';
import {ZodIssueCode, z} from 'zod';
import '../assets/css/question.css';
import CustomSwitch from '../components/CustomSwitch';
import CustomText from '../components/CustomText.styled';
import Div from '../components/Div.styled';
import Icon from '../components/Icon';
import QuestionContentWrapper from '../components/QuestionContentWrapper';
import SpaceLike from '../components/Space';
import InputWrapperWithLabel from '../components/StyledInputWrapperWithLabel';
import TypographyBody from '../components/Typography/TypographyBody';
import {phoneRegex} from '../constants/regex';
import {zodNameValidator} from '../constants/zod';
import useMediaQuery from '../hooks/useMediaQuery';
import useQuestionStore from '../store/navigation';
import {useResultStore} from '../store/responses';
import {useSalesForceObjectStore} from '../store/salesforce';
import {QUESTIONIDS, Question} from '../types/question';
import {QuestionThirteenForm} from '../types/surveyResponse';
import {sendAnalyticPayload} from '../utils/analytic';
import {QuestionData} from '../constants/questions';
import {config} from '../environment';

enum messageTypes {
  INVALID = 'Invalid',
  SUCCESS = 'Success',
}
interface DataToolsAPIResponse {
  message: messageTypes;
  data: {
    isValid: boolean;
    StatusCode: string;
    StatusDescription: string;
    ErrorMessage: string;
  };
}

const phoneValidator = z.string().regex(phoneRegex, 'Please enter valid phone');
const postcodeValidator = z
  .string()
  .regex(/^\d{4}$/, 'Please enter valid postcode');
const emailValidator = z.string().email();

const schema = z.object({
  firstName: zodNameValidator,
  lastName: zodNameValidator,
  email: emailValidator,
  postcode: postcodeValidator,
});

const checkIsValid = async (
  requestUrl: string,
  requestBody: Record<string, string>,
) => {
  try {
    const response = await fetch(requestUrl, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error('Error in checkIsValid:', error);
    throw error;
  }
};

function QuestionThirteenForm(props: Question) {
  const {setQuestionThirteenForm} = useSalesForceObjectStore();
  const {nextStepId, title} = props;
  const {isMobile, isDesktop} = useMediaQuery();
  const {setCurrentQuestion, currentQuestion} = useQuestionStore();
  const {result, updateQuestion} = useResultStore();
  const [form, setForm] = useState<QuestionThirteenForm>({
    callback: false,
    emailAddress: '',
    subscribe: true,
    firstName: result['q1_name'],
    lastName: '',
    phone: '',
    postcode: result['q8_address']?.postcode || '',
  });
  const {subscribe: checkedSubscribe, callback: isCheckedContactSwitch} = form;
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [loadingVerifyEmail, setLoadingVerifyEmail] = useState(false);
  const [loadingVerifyPhone, setLoadingVerifyPhone] = useState(false);

  const [errorMessages, setErrorMessages] = useState<{
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    phone?: string;
    postcode?: string;
  }>({});
  const handleFormChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const validatorMap: Record<
      keyof Required<typeof errorMessages>,
      z.ZodString
    > = {
      firstName: zodNameValidator,
      lastName: zodNameValidator,
      emailAddress: emailValidator,
      phone: phoneValidator,
      postcode: postcodeValidator,
    };

    type FieldName = keyof typeof validatorMap;

    const target = e.target as HTMLInputElement;
    const inputValue = target.value;
    const fieldName = target.name as FieldName;

    const validator = validatorMap[fieldName];

    if (!validator) return;

    const result = validator.safeParse(inputValue);

    if (!result.success) {
      setErrorMessages({
        ...errorMessages,
        [fieldName]: result.error.errors[0]?.message,
      });
    } else {
      setErrorMessages({
        ...errorMessages,
        [fieldName]: undefined,
      });
    }

    setForm({
      ...form,
      [target.name]: inputValue,
    });
  };
  const shouldDisableNextButton = useMemo(() => {
    if (
      errorMessages?.postcode ||
      errorMessages?.firstName ||
      errorMessages?.lastName ||
      errorMessages?.emailAddress
    )
      return true;

    const schemaResult = schema
      .superRefine((_, ctx) => {
        if (
          isCheckedContactSwitch &&
          !phoneValidator.safeParse(form.phone).success
        ) {
          return ctx.addIssue({
            code: ZodIssueCode.custom,
            message: 'Please enter valid phone',
            path: ['phone'],
          });
        }
      })
      .safeParse({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.emailAddress,
        phone: form.phone,
        postcode: form.postcode,
      });

    return !schemaResult.success;
  }, [isCheckedContactSwitch, form, errorMessages]);

  const validateField = async (
    url: string,
    fieldName: string,
    formFieldName: string,
    value: string | undefined,
    errorFieldName: string,
    startLoading: () => void,
    endedLoading: () => void,
  ): Promise<boolean> => {
    if (value) {
      try {
        startLoading();
        const response = await checkIsValid(url, {[fieldName]: value});
        const {message}: DataToolsAPIResponse = await response.json();
        if (message === messageTypes.INVALID) {
          setErrorMessages((prevErrorMessages) => ({
            ...prevErrorMessages,
            [formFieldName]: `Please enter a valid ${errorFieldName}`,
          }));
          return true;
        }
      } catch (error) {
        setErrorMessages((prevErrorMessages) => ({
          ...prevErrorMessages,
          [formFieldName]: `Error validating ${errorFieldName}`,
        }));
        return true;
      } finally {
        endedLoading();
      }
    }
    return false;
  };

  const handleNextButton = async () => {
    const emailValidationFailed: boolean = await validateField(
      config.API_DATA_TOOLS_EMAIL_VALIDATOR_URL,
      'email',
      'emailAddress',
      form.emailAddress,
      'email',
      () => setLoadingVerifyEmail(true),
      () => setLoadingVerifyEmail(false),
    );

    const phoneValidationFailed: boolean = await validateField(
      config.API_DATA_TOOLS_PHONE_NUMBER_VALIDATOR_URL,
      'PhoneNumber',
      'phone',
      form.phone,
      'number',
      () => isCheckedContactSwitch && setLoadingVerifyPhone(true),
      () => setLoadingVerifyPhone(false),
    );

    if (emailValidationFailed || phoneValidationFailed) {
      return;
    }

    updateQuestion({
      id: currentQuestion as typeof QUESTIONIDS.questionThirteenForm,
      value: form,
    });
    updateQuestion({
      id: QUESTIONIDS.questionOneName,
      value: form.firstName,
    });
    setCurrentQuestion(nextStepId);
    setQuestionThirteenForm(form);
    sendAnalyticPayload({
      process: {
        StepName: 'Send results',
        StepNumber: QuestionData[currentQuestion]?.progressValue.toString(),
      },
      question: [
        {
          Question: 'Subscribe to eNewsletter',
          Answer: [
            {
              value: form.subscribe ? 'Yes' : 'No',
              type: 'string',
            },
          ],
        },
        {
          Question: 'Dealer Callback',
          Answer: [
            {
              value: form.callback ? 'Yes' : 'No',
              type: 'string',
            },
          ],
        },
      ],
      user: {
        Location: {
          ZIP: form.postcode,
        },
      },
    });
  };
  const stylePolicy = {
    fontSize: 12,
    width: '100%',
    color: 'white',
    paddingTop: 16,
  };
  return (
    <QuestionContentWrapper
      title={title}
      handleClick={handleNextButton}
      disabled={shouldDisableNextButton}
    >
      <Div>
        <form>
          <SpaceLike direction="vertical" size={isDesktop ? 24 : 16}>
            <InputWrapperWithLabel
              variant={errorMessages?.firstName ? 'error' : 'default'}
            >
              <TextInput
                name={'firstName'}
                key={'first-name'}
                appearance={'default'}
                type="text"
                placeholder="Enter your first name"
                value={form.firstName}
                onChange={handleFormChange}
                label="First Name"
                required
                defaultValue={result?.q1_name}
              />
              {errorMessages?.firstName && (
                <TypographyBody size="small" color="#ff7474">
                  {errorMessages?.firstName}
                </TypographyBody>
              )}
            </InputWrapperWithLabel>
            <InputWrapperWithLabel
              variant={errorMessages?.lastName ? 'error' : 'default'}
            >
              <TextInput
                name={'lastName'}
                key={'last-name'}
                appearance={'default'}
                type="text"
                placeholder="Enter your last name"
                value={form.lastName}
                onChange={handleFormChange}
                label="Last Name"
                required
              />
              {errorMessages?.lastName && (
                <TypographyBody size="small" color="#ff7474">
                  {errorMessages?.lastName}
                </TypographyBody>
              )}
            </InputWrapperWithLabel>
            <InputWrapperWithLabel
              variant={errorMessages?.emailAddress ? 'error' : 'default'}
            >
              <TextInput
                key={'email'}
                name="emailAddress"
                appearance={'default'}
                type="text"
                placeholder="Enter your email"
                value={form.emailAddress}
                onChange={handleFormChange}
                label="Email Address"
                required
              />
              {errorMessages?.emailAddress && (
                <TypographyBody size="small" color="#ff7474">
                  {errorMessages?.emailAddress}
                </TypographyBody>
              )}
              {loadingVerifyEmail && (
                <span
                  style={{
                    position: 'absolute',
                    right: 0,
                    bottom: 10,
                  }}
                >
                  <Spinner variant="default" />
                </span>
              )}
            </InputWrapperWithLabel>

            <InputWrapperWithLabel
              variant={errorMessages?.postcode ? 'error' : 'default'}
            >
              <TextInput
                name="postcode"
                key={'postcode'}
                appearance={'default'}
                type="text"
                placeholder="Enter your postcode"
                value={form.postcode}
                onChange={handleFormChange}
                label="Postcode"
                defaultValue={result ? JSON.stringify(result?.q8_address) : ''}
                required
              />
              {errorMessages?.postcode && (
                <TypographyBody size="small" color="#ff7474">
                  {errorMessages?.postcode}
                </TypographyBody>
              )}
            </InputWrapperWithLabel>
          </SpaceLike>
          <Div
            customStyles={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              margin: '30px 0',
            }}
            width="100%"
          >
            <TypographyBody
              size="small"
              textAlign="left"
              style={{width: isMobile ? '188px' : '267px'}}
            >
              Subscribe to eVW newsletter
            </TypographyBody>
            <CustomSwitch
              name="newsletter"
              checked={checkedSubscribe}
              onChange={() => {
                setForm({...form, subscribe: !checkedSubscribe});
              }}
              labelLeft="No"
              labelRight="Yes"
              customStyle={{
                color: 'white',
                fontSize: isDesktop ? 16 : 14,
              }}
            />
          </Div>
          <Div
            customStyles={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <TypographyBody
              size="small"
              textAlign="left"
              style={{width: isMobile ? '188px' : '267px'}}
            >
              I would like a Dealer to contact me about a Volkswagen Electric
              Vehicle
            </TypographyBody>
            <CustomSwitch
              name="callback"
              checked={form.callback}
              onChange={() =>
                setForm({
                  ...form,
                  callback: !isCheckedContactSwitch,
                })
              }
              labelLeft="No"
              labelRight="Yes"
              customStyle={{
                color: 'white',
                fontSize: isDesktop ? 16 : 14,
              }}
            />
          </Div>

          {isCheckedContactSwitch && (
            <SpaceLike
              direction="vertical"
              size={isDesktop ? 24 : 16}
              customStyles={{marginTop: 30}}
            >
              <InputWrapperWithLabel>
                <TextInput
                  name={'phone'}
                  key={'phone'}
                  appearance={'default'}
                  type="text"
                  placeholder="Enter your phone number"
                  value={form.phone}
                  onChange={handleFormChange}
                  label="Phone Number"
                  required
                />
                {errorMessages?.phone && (
                  <TypographyBody size="small" color="#ff7474">
                    {errorMessages?.phone}
                  </TypographyBody>
                )}
                {loadingVerifyPhone && (
                  <span
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: 10,
                    }}
                  >
                    <Spinner variant="default" />
                  </span>
                )}
              </InputWrapperWithLabel>
            </SpaceLike>
          )}
          <Div customStyles={{marginTop: 40}}>
            <CustomText
              onClick={() => setShowPrivacy(!showPrivacy)}
              customStyles={{
                fontSize: isMobile ? 12 : 14,
                width: '100%',
                color: 'white',
                cursor: 'pointer',
              }}
            >
              <Icon
                icon={showPrivacy ? 'chevron-up' : 'chevron-down'}
                fill="white"
              />{' '}
              Privacy Collection Statement
            </CustomText>
            {showPrivacy && (
              <CustomText customStyles={stylePolicy}>
                Volkswagen Australia may collect, hold and use your personal
                information for a range of purposes, including any purpose
                identified or inferred at the time of collection, including for
                marketing to you, identifying you and any Volkswagen vehicle you
                own, to offer you services, marketing materials, lifestyle
                content or other offers. You are not required to provide your
                personal information, or your real name. However, if you choose
                not to provide us with your personal information or your real
                name, we may not be able to fulfil the purpose(s) described
                above. For full details, see our{' '}
                <a
                  style={stylePolicy}
                  href="https://www.volkswagen.com.au/en/privacy-policy.html"
                  target="_blank"
                >
                  Privacy Policy
                </a>
                .
              </CustomText>
            )}
          </Div>
        </form>
      </Div>
    </QuestionContentWrapper>
  );
}

export default QuestionThirteenForm;
